import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api, supabase } from '../../lib/api';
import { Card, Button, Badge, getLeadTypeBadge, getChannelBadge, Select } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import DncConfirmModal from '../../components/ui/DncConfirmModal';
import ArchiveConfirmModal from '../../components/ui/ArchiveConfirmModal';
import DeleteConfirmModal from '../../components/ui/DeleteConfirmModal';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import LeadNavBar from './LeadNavBar';
import ExclusivityPanel from './ExclusivityPanel';
import EditLeadDrawer from './EditLeadDrawer';
import MergeLeadsModal from '../../components/Leads/MergeLeadsModal';
import LeadRemindersPanel from './LeadRemindersPanel';
import { LEAD_TYPE_LABELS } from '../../lib/leadMeta';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import LogActivityModal from '../../components/Leads/LogActivityModal';
import AiCallerModal from '../../components/Leads/AiCallerModal';
import CallsTab, { CALLS_FILTERS } from '../../components/Leads/CallsTab';
import LeadActivityFeed from '../../components/Leads/LeadActivityFeed';
import EmailComposeModal from '../../components/Leads/EmailComposeModal';
import EmailHistoryList from '../../components/Leads/EmailHistoryList';
import useGmailManualSend, { GmailSendNotice } from '../../components/Leads/useGmailManualSend';
import ScheduleSendControl, { GMAIL_SCHEDULE_NOTE } from '../../components/Leads/ScheduleSendControl';
import ScheduledEmailsList from '../../components/Leads/ScheduledEmailsList';
import { plainTextToHtml, sanitizeEmailHtml } from '../../lib/emailCompose';
import { formatScheduledEt, tomorrowAt9Value, validateScheduleValue } from '../../lib/scheduleSend';
import RoomTypeSelector, { RoomTypeBadge } from '../../components/Dialer/RoomTypeSelector';
import BookAppointmentModal from '../../components/Appointments/BookAppointmentModal';
import Softphone from '../../components/Dialer/Softphone';
import SMSThread from '../../components/SMS/SMSThread';
import SMSInputBar from '../../components/SMS/SMSInputBar';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { formatDistanceToNow, format, differenceInYears, differenceInHours, isSameYear, parseISO } from 'date-fns';
import {
  calculateNetWorth,
  calculateEstateNeedsAttentionCount,
} from '../../lib/financialAnalysisUtils';
import {
  getActiveDirectoryUsers,
  loadUsersDirectory,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import { useVisiblePolling } from '../../lib/useVisiblePolling';

const {
  FiPhone, FiMail, FiUser, FiClock, FiActivity,
  FiEdit2, FiArrowLeft, FiMessageSquare,
  FiTool, FiArchive, FiTrash2, FiMapPin, FiPhoneCall, FiCalendar, FiZap, FiGitMerge, FiPieChart,
  FiMaximize2,
} = FiIcons;

const APPOINTMENT_TYPE_LABELS = {
  intro_call: 'Intro Call',
  presentation: 'Presentation',
  follow_up: 'Follow-Up',
  closing_call: 'Closing Call',
};

function AppointmentTypeBadge({ type }) {
  const label = APPOINTMENT_TYPE_LABELS[type] || String(type || '').replace(/_/g, ' ') || '—';
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#00AAFF]/12 text-[#00AAFF]">
      {label}
    </span>
  );
}

function AppointmentStatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const map = {
    scheduled: { variant: 'primary', label: 'Scheduled' },
    confirmed: { variant: 'success', label: 'Confirmed' },
    completed: { variant: 'default', label: 'Completed' },
    cancelled: { variant: 'danger', label: 'Cancelled' },
    no_show: { variant: 'warning', label: 'No Show' },
  };
  const x = map[s] || { variant: 'default', label: status || '—' };
  return <Badge variant={x.variant}>{x.label}</Badge>;
}

function EnrollmentStatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const map = {
    active: { variant: 'success', label: 'Active' },
    completed: { variant: 'default', label: 'Completed' },
    cancelled: { variant: 'danger', label: 'Cancelled' },
  };
  const x = map[s] || { variant: 'default', label: status || '—' };
  return <Badge variant={x.variant}>{x.label}</Badge>;
}

function formatEnrollmentTimestamp(iso) {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'MMM d, yyyy h:mm a');
  } catch {
    return '—';
  }
}

const LEAD_TABS = [
  { id: 'activity', label: 'Activity Feed' },
  { id: 'notes', label: 'Notes' },
  { id: 'reminders', label: 'Reminders' },
  { id: 'calls', label: 'Calls' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
  { id: 'sequences', label: 'Sequences' },
  { id: 'sessions', label: 'Sessions' },
];

/** `?tab=` deep link → { tab, callsFilter }. `ai_calls` predates the merged Calls tab. */
function tabFromSearch(search) {
  try {
    const requested = new URLSearchParams(search).get('tab');
    if (requested === 'ai_calls') return { tab: 'calls', callsFilter: 'ai' };
    if (LEAD_TABS.some((t) => t.id === requested)) return { tab: requested, callsFilter: null };
  } catch {
    /* fall through */
  }
  return null;
}

const REFRESH_INTERVAL = 3 * 60 * 1000;
const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin']);
const CO_WORKER_ADMIN_ROLES = new Set(['admin', 'super_admin']);

function coWorkerDisplayName(lead) {
  return resolveDisplayName(lead?.co_worker_id, lead?.co_worker) || '—';
}

function LanguageBadge({ language }) {
  const code = (language === 'es' ? 'es' : 'en').toUpperCase();
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500"
      title={code === 'ES' ? 'Español' : 'English'}
    >
      {code}
    </span>
  );
}

function LeadPeopleSummary({ lead }) {
  const parts = [];
  const ownerName = resolveDisplayName(lead?.created_by, lead?.owner);
  if (ownerName) parts.push({ label: 'Owner', name: ownerName });
  const assignedName = resolveDisplayName(lead?.exclusive_to, lead?.exclusive_holder);
  if (lead?.exclusive_to && lead.exclusive_to !== lead.created_by && assignedName) {
    parts.push({ label: 'Assigned To', name: assignedName });
  }
  const coWorkerName = resolveDisplayName(lead?.co_worker_id, lead?.co_worker);
  if (coWorkerName) parts.push({ label: 'Co-Worker', name: coWorkerName });

  const prevOwnerDate = lead?.ownership_transferred_at
    ? (() => {
        try {
          const d = parseISO(lead.ownership_transferred_at);
          return Number.isNaN(d.getTime()) ? null : format(d, 'MMM d, yyyy');
        } catch {
          return null;
        }
      })()
    : null;

  if (!parts.length && !lead?.previous_owner_name) return null;

  return (
    <div className="mt-1 space-y-1">
      {parts.length > 0 && (
        <p className="text-xs text-gray-500">
          {parts.map(({ label, name }, i) => (
            <span key={label}>
              {i > 0 ? ' · ' : ''}
              <span className="text-gray-400">{label}: </span>
              <span className="font-medium text-gray-700">{name}</span>
            </span>
          ))}
        </p>
      )}
      {lead?.previous_owner_name && (
        <div className="text-xs text-gray-500">
          <span className="text-amber-600 font-medium">Prev. Owner: </span>
          <span>
            {lead.previous_owner_name}
            {lead.previous_owner_email ? ` (${lead.previous_owner_email})` : ''}
          </span>
          {prevOwnerDate && (
            <p className="text-[11px] text-gray-400 mt-0.5">Transferred {prevOwnerDate}</p>
          )}
        </div>
      )}
    </div>
  );
}

function formatLastCalledDisplay(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  const now = new Date();
  if (differenceInHours(now, d) < 48) {
    return formatDistanceToNow(d, { addSuffix: true });
  }
  return format(d, isSameYear(d, now) ? 'MMM d, h:mm a' : 'MMM d, yyyy h:mm a');
}

function toDatetimeLocalValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const LOG_CALL_OUTCOMES = [
  { value: 'answered', label: 'Answered' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'callback_requested', label: 'Callback Requested' },
  { value: 'booked_meeting', label: 'Booked Meeting' },
  { value: 'closed_business', label: 'Closed Business' },
];

const SMS_QUICK_REPLY_LABELS = ['1st Touch', '2nd Attempt', '3rd Attempt', 'Re-engage'];

const SMS_QUICK_REPLY_TEMPLATES = {
  LD: {
    en: [
      "Hey {firstName}, it's {agentName}. Just getting back to you about that request you made online recently with a company called ETHOS about possibly getting life insurance to protect your family. Just called and got your voicemail... Were you looking for something specific, or just exploring your options?",
      "Hey {firstName}, just called again and missed you. Just reaching back out regarding your recent inquiry about potential life insurance with a company called ETHOS. Is later today better for you, or would another day work better?",
      "Hey {firstName}, just called again and missed you. Also sent a few messages regarding your recent inquiry about potential life insurance options with a company called ETHOS and haven't heard back… Where should we go from here…?",
      "Hey {firstName}, it's {agentName}. I was going through my notes and realized we never connected to explore your life insurance options… Did you give up on getting protection in place, or what ended up happening over there?",
    ],
    es: [
      'Hola {firstName}, soy {agentName}. Te escribo porque recientemente solicitaste información a través de una compañía llamada ETHOS sobre la posibilidad de obtener un seguro de vida para proteger a tu familia. Te llamé y no te encontré… ¿Estabas buscando algo específico o solo explorando opciones?',
      'Hola {firstName}, te llamé de nuevo y no pude comunicarme. Te escribo en relación a tu consulta reciente sobre opciones de seguro de vida con ETHOS. ¿Te queda mejor más tarde hoy, o prefieres otro día?',
      'Hola {firstName}, te llamé varias veces y también te envié mensajes sobre tu interés en opciones de seguro de vida con ETHOS, pero no he tenido respuesta… ¿Cómo seguimos desde aquí?',
      'Hola {firstName}, soy {agentName}. Estaba revisando mis notas y me di cuenta de que nunca pudimos conectar para explorar tus opciones de seguro de vida… ¿Decidiste no continuar con la protección, o qué pasó?',
    ],
  },
  IM: {
    en: [
      "Hey {firstName}, it's {agentName}! I wanted to reach out because I've been working with some people in your area on some financial concepts and strategies that have been really making a difference. Just called and missed you — are you open to a quick conversation?",
      "Hey {firstName}, tried reaching you again and missed you. I work with families and professionals helping them build better financial strategies — wanted to see if it's something you'd be open to exploring. Is later today good, or does another day work better?",
      "Hey {firstName}, tried a few times and haven't been able to connect. I've been sharing some financial concepts with people in your area and wanted to make sure you had the chance to hear about it… Where should we go from here?",
      "Hey {firstName}, it's {agentName}. I was going through my list and realized we never got a chance to connect about the financial strategies I've been sharing with people. Did life just get busy, or what ended up happening?",
    ],
    es: [
      'Hola {firstName}, soy {agentName}! Quería comunicarme contigo porque he estado compartiendo algunos conceptos y estrategias financieras con personas en tu área que han marcado una gran diferencia. Te llamé y no te encontré — ¿estarías abierto/a a una conversación rápida?',
      'Hola {firstName}, intenté contactarte de nuevo y no pude. Trabajo con familias y profesionales ayudándoles a construir mejores estrategias financieras — quería saber si es algo que te interesaría explorar. ¿Te queda mejor más tarde hoy, o prefieres otro día?',
      'Hola {firstName}, he intentado varias veces y no hemos podido conectar. He estado compartiendo algunos conceptos financieros con personas en tu área y quería asegurarme de que tuvieras la oportunidad de escuchar sobre esto… ¿Cómo seguimos?',
      'Hola {firstName}, soy {agentName}. Estaba revisando mi lista y me di cuenta de que nunca pudimos conectar sobre las estrategias financieras que he estado compartiendo. ¿Se te complicó la vida, o qué pasó?',
    ],
  },
  AM: {
    en: [
      "Hey {firstName}, it's {agentName}! I got your contact info through a referral and wanted to reach out — I work with professionals in your field on some financial strategies that tend to be really relevant to what you do. Just called and missed you — are you open to a quick chat?",
      "Hey {firstName}, tried reaching you again and missed you. I was referred to you and wanted to connect about some advanced financial strategies that are a great fit for professionals like yourself. Is later today better, or would another day work?",
      "Hey {firstName}, tried a few times now and haven't been able to connect. Whoever referred me to you spoke highly of you — I'd hate for us to not at least have a quick conversation about what I've been sharing… Where should we go from here?",
      "Hey {firstName}, it's {agentName}. I was going through my notes and realized we never got a chance to connect. I was referred to you a little while back about some financial strategies I think you'd find valuable — did life just get busy, or what ended up happening?",
    ],
    es: [
      'Hola {firstName}, soy {agentName}! Me dieron tu contacto a través de una referencia y quería comunicarme — trabajo con profesionales en tu campo en estrategias financieras avanzadas que suelen ser muy relevantes para lo que haces. Te llamé y no te encontré — ¿estarías disponible para una conversación rápida?',
      'Hola {firstName}, intenté contactarte de nuevo y no pude. Me refirieron a ti y quería conectar sobre algunas estrategias financieras avanzadas que son ideales para profesionales como tú. ¿Te queda mejor más tarde hoy, o prefieres otro día?',
      'Hola {firstName}, he intentado varias veces y no hemos podido conectar. Quien me refirió a ti habló muy bien de ti — no quisiera que nos perdiéramos de al menos tener una conversación rápida… ¿Cómo seguimos?',
      'Hola {firstName}, soy {agentName}. Estaba revisando mis notas y me di cuenta de que nunca pudimos conectar. Me refirieron a ti hace un tiempo sobre algunas estrategias financieras que creo que serían de gran valor para ti — ¿se te complicó la vida, o qué pasó?',
    ],
  },
};

function resolveSmsTemplate(template, firstName, agentName) {
  const fn = firstName?.trim() || '';
  const ag = agentName?.trim() || '';
  return template.replace(/\{firstName\}/g, fn).replace(/\{agentName\}/g, ag);
}

function buildSmsQuickReplyTemplates(channel, language, firstName, agentName) {
  const channelKey = SMS_QUICK_REPLY_TEMPLATES[channel] ? channel : 'LD';
  const langKey = language === 'es' ? 'es' : 'en';
  const templates = SMS_QUICK_REPLY_TEMPLATES[channelKey][langKey];
  return SMS_QUICK_REPLY_LABELS.map((label, index) => ({
    key: `t${index + 1}`,
    label,
    text: resolveSmsTemplate(templates[index], firstName, agentName),
  }));
}

const PM_FIELDS = [
  'event_title',
  'payment_status',
  'has_pm_account',
  'capturing_agent_name',
  'capturing_agent_email',
  'pm_form_responses',
  'pm_quiz_responses',
  'pm_survey_responses',
  'pm_activities',
];

function hasPmData(lead) {
  return PM_FIELDS.some((f) => {
    const v = lead?.[f];
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    return true;
  });
}

function PmResponseBlock({ title, content }) {
  const [open, setOpen] = useState(false);
  if (!content || !String(content).trim()) return null;
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span className="text-sm font-medium text-gray-700">{title}</span>
        <SafeIcon
          icon={open ? FiIcons.FiChevronUp : FiIcons.FiChevronDown}
          className="w-4 h-4 text-gray-400"
        />
      </button>
      {open && (
        <pre className="px-4 py-3 text-xs font-mono text-gray-700 whitespace-pre-wrap break-words bg-white border-t border-gray-100 max-h-72 overflow-y-auto">
          {String(content)}
        </pre>
      )}
    </div>
  );
}

function ProsperityMentorsSection({ lead }) {
  const [open, setOpen] = useState(false);
  if (!hasPmData(lead)) return null;

  const hasAccount = lead.has_pm_account;
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
      >
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiAward} className="text-primary" />
          Academy Data
        </h3>
        <SafeIcon
          icon={open ? FiIcons.FiChevronUp : FiIcons.FiChevronDown}
          className="w-5 h-5 text-gray-400"
        />
      </button>

      {open && (
        <div className="px-6 pb-6 pt-1 space-y-5 border-t border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Event Title</p>
              <p className="text-sm text-gray-800">{lead.event_title || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Payment Status</p>
              <p className="text-sm text-gray-800">{lead.payment_status || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Has PM Account</p>
              {hasAccount === null || hasAccount === undefined ? (
                <span className="text-sm text-gray-800">—</span>
              ) : (
                <Badge variant={hasAccount ? 'success' : 'default'}>{hasAccount ? 'Yes' : 'No'}</Badge>
              )}
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Capturing Agent</p>
              <p className="text-sm text-gray-800">{lead.capturing_agent_name || '—'}</p>
              {lead.capturing_agent_email && (
                <p className="text-xs text-gray-500 truncate">{lead.capturing_agent_email}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <PmResponseBlock title="Form Responses" content={lead.pm_form_responses} />
            <PmResponseBlock title="Quiz Responses" content={lead.pm_quiz_responses} />
            <PmResponseBlock title="Survey Responses" content={lead.pm_survey_responses} />
            <PmResponseBlock title="Activities" content={lead.pm_activities} />
          </div>
        </div>
      )}
    </Card>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  useUsersDirectory();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    () => tabFromSearch(window.location.search)?.tab ?? 'activity',
  );
  const [callsFilter, setCallsFilter] = useState(
    () => tabFromSearch(window.location.search)?.callsFilter ?? 'all',
  );
  const [dialerOpen, setDialerOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [note, setNote] = useState('');
  const [showDncModal, setShowDncModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [activityRefreshing, setActivityRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignRoster, setAssignRoster] = useState([]);
  const [assignSetterId, setAssignSetterId] = useState('');
  const [assignCloserId, setAssignCloserId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignLoadingRoster, setAssignLoadingRoster] = useState(false);
  const [coWorkerSaving, setCoWorkerSaving] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicatePickerOpen, setDuplicatePickerOpen] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeCandidateB, setMergeCandidateB] = useState(null);
  const [mergeCandidateLoading, setMergeCandidateLoading] = useState(false);
  const [smsThread, setSmsThread] = useState([]);
  const [smsThreadLoading, setSmsThreadLoading] = useState(false);
  const [dialingStats, setDialingStats] = useState({ totalCalls: 0, lastCalledAt: null, callsLast5Min: 0 });
  const [quickSmsBody, setQuickSmsBody] = useState('');
  const [quickSmsSending, setQuickSmsSending] = useState(false);
  const [smsLanguage, setSmsLanguage] = useState('en');
  const quickSmsTextareaRef = useRef(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeBody, setComposeBody] = useState('');
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduleValue, setScheduleValue] = useState(() => tomorrowAt9Value());
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [logCallOutcome, setLogCallOutcome] = useState('');
  const [logCallDirection, setLogCallDirection] = useState('outbound');
  const [logCallMinutes, setLogCallMinutes] = useState('0');
  const [logCallSeconds, setLogCallSeconds] = useState('0');
  const [logCallAtLocal, setLogCallAtLocal] = useState(() => toDatetimeLocalValue(new Date()));
  const [logCallNotes, setLogCallNotes] = useState('');
  const [logCallSaving, setLogCallSaving] = useState(false);
  const [logCallRoomType, setLogCallRoomType] = useState(null);
  const [roomTypePopoverOpen, setRoomTypePopoverOpen] = useState(false);
  const [roomTypeSaving, setRoomTypeSaving] = useState(false);
  const roomTypePopoverRef = useRef(null);
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [bookModalAppt, setBookModalAppt] = useState(null);
  const [logActivityOpen, setLogActivityOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [fna, setFna] = useState(null);
  const [fnaLoading, setFnaLoading] = useState(true);
  const [fnaError, setFnaError] = useState(null);
  const [fnaStarting, setFnaStarting] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [enrollModalOptions, setEnrollModalOptions] = useState([]);
  const [enrollModalLoading, setEnrollModalLoading] = useState(false);
  const [selectedSequenceId, setSelectedSequenceId] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [cancellingEnrollmentId, setCancellingEnrollmentId] = useState(null);
  const [aiCallerModalOpen, setAiCallerModalOpen] = useState(false);
  const [thoughtlyCalls, setThoughtlyCalls] = useState([]);
  const [thoughtlyAgents, setThoughtlyAgents] = useState([]);
  const [thoughtlyLoading, setThoughtlyLoading] = useState(false);
  const [pendingTransferRequest, setPendingTransferRequest] = useState(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferNotes, setTransferNotes] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  // Determine back destination: recruits if lead_type is recruit, else leads
  const getBackPath = useCallback(() => {
    if (lead?.lead_type === 'recruit') return '/recruits';
    return '/leads';
  }, [lead]);

  /**
   * Everything here except the Thoughtly agent roster is independent of
   * everything else, so it all goes out at once. Previously each block awaited
   * the one before it, which made the page cost roughly nine sequential round
   * trips instead of two.
   */
  const loadLead = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setFnaLoading(true);
      setThoughtlyLoading(true);

      const [
        data,
        me,
        appts,
        analysisResult,
        statsResult,
        thoughtlyCallsResult,
      ] = await Promise.all([
        api.leads.getById(id),
        api.auth.getCurrentUser(),
        api.appointments.getByLead(id).catch(() => []),
        api.financialAnalysis.getByLeadId(id).then(
          (value) => ({ ok: true, value }),
          (error) => ({ ok: false, error }),
        ),
        api.callLogs.getDialingStatsForLead(id).then(
          (value) => ({ ok: true, value }),
          (error) => ({ ok: false, error }),
        ),
        api.thoughtly.getCalls(id).then(
          (value) => ({ ok: true, value }),
          (error) => ({ ok: false, error }),
        ),
        // Expiry is a side effect the rest of the page does not read, so it no
        // longer blocks the initial paint.
        api.exclusivity.checkAndExpireExclusivity(id).catch((expErr) => {
          console.error('[lead-detail] checkAndExpireExclusivity', expErr);
        }),
      ]);

      setLead(data);
      setViewer(me);
      setAppointments(Array.isArray(appts) ? appts : []);
      setLastRefreshed(new Date());

      if (analysisResult.ok) {
        setFna(analysisResult.value);
        setFnaError(null);
      } else {
        console.error('[lead-detail] financialAnalysis', analysisResult.error);
        setFna(null);
        setFnaError(analysisResult.error?.message || 'Failed to load financial analysis');
      }
      setFnaLoading(false);

      if (statsResult.ok) {
        setDialingStats(statsResult.value);
      } else {
        console.error('[lead-detail] getDialingStatsForLead', statsResult.error);
      }

      if (thoughtlyCallsResult.ok) {
        setThoughtlyCalls(thoughtlyCallsResult.value);
      } else {
        console.error('[lead-detail] thoughtly', thoughtlyCallsResult.error);
        setThoughtlyCalls([]);
      }

      // Only this depends on the loaded lead.
      if (data.agency_id) {
        try {
          setThoughtlyAgents(await api.thoughtly.getAgents(data.agency_id));
        } catch (e) {
          console.error('[lead-detail] thoughtly agents', e);
          setThoughtlyAgents([]);
        }
      } else {
        setThoughtlyAgents([]);
      }
      setThoughtlyLoading(false);
    } catch (err) {
      setFnaLoading(false);
      setThoughtlyLoading(false);
      if (!silent) {
        addToast({ type: 'error', title: 'Failed to load lead' });
        navigate('/leads');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate, addToast]);

  /**
   * Poll path. Deliberately narrower than loadLead: it refetches only what can
   * actually change while the page sits open. The signed-in viewer, the Thoughtly
   * agent roster and the financial analysis are not re-requested.
   */
  const refreshActivity = useCallback(async () => {
    setActivityRefreshing(true);
    try {
      const [data, appts, statsResult, thoughtlyCallsResult] = await Promise.all([
        api.leads.getById(id),
        api.appointments.getByLead(id).catch(() => []),
        api.callLogs.getDialingStatsForLead(id).then(
          (value) => ({ ok: true, value }),
          (error) => ({ ok: false, error }),
        ),
        api.thoughtly.getCalls(id).then(
          (value) => ({ ok: true, value }),
          (error) => ({ ok: false, error }),
        ),
      ]);
      setLead(data);
      setAppointments(Array.isArray(appts) ? appts : []);
      setLastRefreshed(new Date());
      if (statsResult.ok) setDialingStats(statsResult.value);
      if (thoughtlyCallsResult.ok) setThoughtlyCalls(thoughtlyCallsResult.value);
    } catch { /* silent */ } finally {
      setActivityRefreshing(false);
    }
  }, [id]);

  useEffect(() => { loadLead(); }, [loadLead]);

  useEffect(() => {
    if (!id) {
      setPendingTransferRequest(null);
      return;
    }
    let cancelled = false;
    api.ownershipTransfers
      .getPendingForLead(id)
      .then((row) => {
        if (!cancelled) setPendingTransferRequest(row);
      })
      .catch(() => {
        if (!cancelled) setPendingTransferRequest(null);
      });
    return () => { cancelled = true; };
  }, [id, lead?.created_by]);

  const isTrainerOfOwner = Boolean(
    viewer && lead?.owner?.trainer_id && lead.owner.trainer_id === viewer.id,
  );
  const isLeadOwner = Boolean(viewer && lead?.created_by === viewer.id);
  const canRequestOwnershipTransfer =
    isTrainerOfOwner && !isLeadOwner && !pendingTransferRequest;

  const handleSubmitTransferRequest = async () => {
    if (!lead?.agency_id || !lead?.created_by) return;
    setTransferSubmitting(true);
    try {
      await api.ownershipTransfers.request({
        leadId: id,
        agencyId: lead.agency_id,
        currentOwnerId: lead.created_by,
        notes: transferNotes,
      });
      addToast({ type: 'success', title: 'Transfer request submitted' });
      setTransferModalOpen(false);
      setTransferNotes('');
      const pending = await api.ownershipTransfers.getPendingForLead(id);
      setPendingTransferRequest(pending);
      window.dispatchEvent(new CustomEvent('transfer-pending-change'));
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Transfer request failed',
        message: err.message,
      });
    } finally {
      setTransferSubmitting(false);
    }
  };

  useEffect(() => {
    if (!roomTypePopoverOpen) return undefined;
    const onDocClick = (e) => {
      if (roomTypePopoverRef.current && !roomTypePopoverRef.current.contains(e.target)) {
        setRoomTypePopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [roomTypePopoverOpen]);

  const handleLeadRoomTypeChange = async (nextType) => {
    if (!id) return;
    setRoomTypeSaving(true);
    try {
      const updated = await api.roomType.updateLeadRoomType(id, nextType);
      setLead((prev) => (prev ? { ...prev, room_type: updated.room_type } : prev));
      setRoomTypePopoverOpen(false);
      addToast({ type: 'success', title: 'Lead type updated' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not update lead type', message: err.message });
    } finally {
      setRoomTypeSaving(false);
    }
  };

  useEffect(() => {
    if (!id || !supabase) return undefined;
    const refreshFromCallLogs = async () => {
      try {
        const [data, stats, appts] = await Promise.all([
          api.leads.getById(id),
          api.callLogs.getDialingStatsForLead(id),
          api.appointments.getByLead(id).catch(() => []),
        ]);
        setLead(data);
        setDialingStats(stats);
        setAppointments(Array.isArray(appts) ? appts : []);
        setLastRefreshed(new Date());
      } catch {
        /* ignore */
      }
    };
    const channel = supabase
      .channel(`call-logs-lead-${id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'call_logs', filter: `lead_id=eq.${id}` },
        refreshFromCallLogs,
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  useEffect(() => {
    if (smsModalOpen && lead) {
      setSmsBody(`${lead.first_name} ${lead.last_name}`.trim());
    }
  }, [smsModalOpen, lead]);

  const bookModalWasOpenRef = useRef(false);
  useEffect(() => {
    if (bookModalOpen) {
      bookModalWasOpenRef.current = true;
      return;
    }
    // Resume after Book Appointment closes; refetch once so the
    // appointment_booked note appears without waiting for the next tick.
    if (bookModalWasOpenRef.current) {
      bookModalWasOpenRef.current = false;
      void refreshActivity();
    }
  }, [refreshActivity, bookModalOpen]);

  useVisiblePolling(refreshActivity, REFRESH_INTERVAL, Boolean(id) && !bookModalOpen);

  useEffect(() => {
    const linked = tabFromSearch(location.search);
    if (!linked) return;
    setActiveTab(linked.tab);
    if (linked.callsFilter) setCallsFilter(linked.callsFilter);
  }, [location.search]);

  const openTab = useCallback((tab, options) => {
    if (options?.callsFilter && CALLS_FILTERS.some((f) => f.id === options.callsFilter)) {
      setCallsFilter(options.callsFilter);
    }
    setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (activeTab !== 'sessions' || !id) return;
    setSessionsLoading(true);
    api.workspaceSessions.getByLead(id)
      .then((data) => setSessions(data || []))
      .catch((err) => console.error('[lead-detail] failed to load sessions', err))
      .finally(() => setSessionsLoading(false));
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab !== 'sms' || !id) return;
    let channel;

    setSmsThreadLoading(true);
    api.smsMessages.getThread(id)
      .then((data) => setSmsThread(data || []))
      .catch((err) => console.error('[lead-detail] failed to load sms thread', err))
      .finally(() => setSmsThreadLoading(false));

    api.smsMessages.markThreadRead(id)
      .then(() => {
        window.dispatchEvent(new Event('sms-unread-change'));
      })
      .catch(() => {});

    if (supabase) {
      channel = supabase
        .channel(`sms-lead-${id}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'sms_messages', filter: `lead_id=eq.${id}` },
          (payload) => {
            setSmsThread((prev) => {
              if (prev.some((m) => m.id === payload.new.id)) return prev;
              return [...prev, payload.new];
            });
            if (payload.new.direction === 'inbound') {
              api.smsMessages.markThreadRead(id)
                .then(() => {
                  window.dispatchEvent(new Event('sms-unread-change'));
                })
                .catch(() => {});
            }
          },
        )
        .subscribe();
    }

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [activeTab, id]);

  const refreshEmailLogs = useCallback(async () => {
    if (!id) return;
    try {
      const rows = await api.emailLogs.getByLead(id);
      setEmailLogs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('[lead-detail] failed to refresh email logs', err);
    }
  }, [id]);

  const refreshScheduledEmails = useCallback(async () => {
    if (!id) return;
    try {
      const rows = await api.scheduledEmails.listByLead(id);
      setScheduledEmails(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('[lead-detail] failed to load scheduled emails', err);
    }
  }, [id]);

  const clearEmailDraft = useCallback(() => {
    setEmailSubject('');
    setEmailBody('');
    setComposeBody('');
    setComposeOpen(false);
  }, []);

  const handleScheduleModeChange = useCallback((mode) => {
    setScheduleMode(mode);
    if (mode === 'schedule' && !validateScheduleValue(scheduleValue).iso) {
      setScheduleValue(tomorrowAt9Value());
    }
  }, [scheduleValue]);

  const handleGmailLogged = useCallback(async () => {
    clearEmailDraft();
    await refreshEmailLogs();
  }, [clearEmailDraft, refreshEmailLogs]);

  const gmail = useGmailManualSend({
    leadId: id,
    toEmail: lead?.email?.trim() || '',
    onLogged: handleGmailLogged,
  });

  useEffect(() => {
    if (activeTab !== 'email' || !id) return;
    let cancelled = false;
    setEmailLogsLoading(true);
    api.emailLogs
      .getByLead(id)
      .then((rows) => {
        if (!cancelled) setEmailLogs(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => console.error('[lead-detail] failed to load email logs', err))
      .finally(() => {
        if (!cancelled) setEmailLogsLoading(false);
      });
    api.scheduledEmails
      .listByLead(id)
      .then((rows) => {
        if (!cancelled) setScheduledEmails(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => console.error('[lead-detail] failed to load scheduled emails', err));
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  const loadEnrollments = useCallback(async () => {
    if (!id) return;
    setEnrollmentsLoading(true);
    try {
      const rows = await api.enrollments.getByLead(id);
      setEnrollments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('[lead-detail] loadEnrollments', err);
      addToast({
        type: 'error',
        title: 'Could not load sequences',
        message: err?.message || 'Try again shortly.',
      });
      setEnrollments([]);
    } finally {
      setEnrollmentsLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    if (activeTab !== 'sequences' || !id) return;
    void loadEnrollments();
  }, [activeTab, id, loadEnrollments]);

  const openEnrollModal = async () => {
    if (!lead?.agency_id || !id) return;
    setEnrollModalOpen(true);
    setSelectedSequenceId('');
    setEnrollModalLoading(true);
    try {
      const [allSequences, currentEnrollments] = await Promise.all([
        api.sequences.getAll(lead.agency_id),
        api.enrollments.getByLead(id),
      ]);
      const activeSequenceIds = new Set(
        (currentEnrollments ?? [])
          .filter((e) => e.status === 'active')
          .map((e) => e.sequence_id),
      );
      setEnrollModalOptions(
        (allSequences ?? []).filter(
          (seq) => seq.is_active !== false && !activeSequenceIds.has(seq.id),
        ),
      );
    } catch (err) {
      console.error('[lead-detail] openEnrollModal', err);
      addToast({
        type: 'error',
        title: 'Could not load sequences',
        message: err?.message || 'Try again.',
      });
      setEnrollModalOptions([]);
    } finally {
      setEnrollModalLoading(false);
    }
  };

  const handleEnrollInSequence = async () => {
    if (!selectedSequenceId || !lead?.agency_id || !id) return;
    setEnrolling(true);
    try {
      await api.enrollments.enroll(selectedSequenceId, id, lead.agency_id, viewer?.id ?? null);
      addToast({ type: 'success', title: 'Lead enrolled in sequence' });
      setEnrollModalOpen(false);
      await loadEnrollments();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Enrollment failed',
        message: err?.message || 'Try again.',
      });
    } finally {
      setEnrolling(false);
    }
  };

  const handleCancelEnrollment = async (enrollmentId) => {
    setCancellingEnrollmentId(enrollmentId);
    try {
      const updated = await api.enrollments.cancel(enrollmentId);
      setEnrollments((prev) => prev.map((e) => (e.id === enrollmentId ? updated : e)));
      addToast({ type: 'success', title: 'Enrollment cancelled' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not cancel enrollment',
        message: err?.message || 'Try again.',
      });
    } finally {
      setCancellingEnrollmentId(null);
    }
  };

  const commitStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api.leads.update(id, { status: newStatus });
      setLead(prev => ({ ...prev, status: newStatus }));
      void loadEnrollments();
      addToast({ type: 'success', title: 'Status updated', message: `Lead marked as "${newStatus.replace(/_/g, ' ')}"` });
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to update status', message: err.message });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus === 'dnc') {
      setPendingStatus('dnc');
      setShowDncModal(true);
    } else {
      commitStatusChange(newStatus);
    }
  };

  const handleDncConfirm = async () => {
    setShowDncModal(false);
    await commitStatusChange(pendingStatus);
    setPendingStatus(null);
  };

  const handleDncCancel = () => {
    setShowDncModal(false);
    setPendingStatus(null);
  };

  const handleSendSms = async () => {
    if (!smsBody.trim() || !lead?.phone) return;
    setSmsSending(true);
    try {
      const user = await api.auth.getCurrentUser();
      const preferredDialer =
        user?.preferred_dialer === 'telnyx' || user?.preferred_dialer === 'twilio'
          ? user.preferred_dialer
          : 'twilio';
      await api.calls.sendSms({
        to: lead.phone.trim(),
        message: smsBody.trim(),
        lead_id: id,
        agency_id: lead.agency_id,
        preferredDialer,
      });
      addToast({
        type: 'success',
        title: 'SMS sent',
        message: `Sent via ${preferredDialer === 'telnyx' ? 'Telnyx' : 'Twilio'}`,
      });
      setSmsModalOpen(false);
      await loadLead(true);
    } catch (err) {
      addToast({ type: 'error', title: 'SMS failed', message: err.message });
    } finally {
      setSmsSending(false);
    }
  };

  const handleSaveNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.leads.update(id, { notes: note });
      setLead(prev => ({ ...prev, notes: note }));
      setNote('');
      addToast({ type: 'success', title: 'Note saved' });
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to save note', message: err.message });
    } finally {
      setSavingNote(false);
    }
  };

  const canManageAssignments = viewer && MANAGER_ROLES.has(viewer.role);
  const canManageCoWorker = Boolean(
    viewer &&
    lead &&
    (lead.created_by === viewer.id || CO_WORKER_ADMIN_ROLES.has(viewer.role)),
  );
  const canDeleteLead = viewer && MANAGER_ROLES.has(viewer.role);
  const canArchiveLead =
    canManageAssignments ||
    (viewer && lead?.assignments?.some((a) => a.user_id === viewer.id));

  const handleArchiveConfirm = async () => {
    setShowArchiveModal(false);
    try {
      await api.leads.archive(id);
      addToast({ type: 'success', title: 'Lead archived' });
      navigate(getBackPath());
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to archive', message: err.message });
    }
  };

  const handleFindDuplicate = async () => {
    if (!lead?.id || !viewer?.agency_id) return;
    setDuplicateMessage('');
    setDuplicatesLoading(true);
    try {
      const matches = await api.leads.findDuplicates(lead.id, viewer.agency_id);
      if (!matches.length) {
        setDuplicateMessage('No duplicates found for this lead');
        setDuplicatePickerOpen(false);
        return;
      }
      setDuplicates(matches);
      setDuplicatePickerOpen(true);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not search for duplicates',
        message: err.message,
      });
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleSelectDuplicateForMerge = async (dup) => {
    setDuplicatePickerOpen(false);
    setMergeCandidateLoading(true);
    try {
      const full = await api.leads.getById(dup.id);
      setMergeCandidateB(full);
      setMergeModalOpen(true);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not load lead',
        message: err.message,
      });
    } finally {
      setMergeCandidateLoading(false);
    }
  };

  const handleMergeComplete = (survivingLead) => {
    setMergeModalOpen(false);
    setMergeCandidateB(null);
    if (survivingLead?.id && survivingLead.id !== id) {
      navigate(`/leads/${survivingLead.id}`, {
        state: location.state?.leadIds ? { leadIds: location.state.leadIds, currentIndex: location.state.currentIndex } : undefined,
      });
    } else {
      void loadLead(true);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteInProgress(true);
    try {
      await api.leads.delete(id);
      setShowDeleteModal(false);
      addToast({ type: 'success', title: 'Lead permanently deleted' });
      navigate(getBackPath());
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to delete', message: err.message });
    } finally {
      setIsDeleteInProgress(false);
    }
  };

  const openAssignModal = async () => {
    if (!canManageAssignments || !lead?.agency_id) {
      addToast({
        type: 'error',
        title: 'Cannot manage assignments',
        message: 'Only managers and admins can assign agents.',
      });
      return;
    }
    const setterRow = lead.assignments?.find((a) => a.assignment_role === 'setter');
    const closerRow = lead.assignments?.find((a) => a.assignment_role === 'closer');
    setAssignSetterId(setterRow?.user_id ?? '');
    setAssignCloserId(closerRow?.user_id ?? '');
    setAssignModalOpen(true);
    setAssignLoadingRoster(true);
    try {
      const roster = await loadUsersDirectory();
      setAssignRoster(getActiveDirectoryUsers(roster));
    } catch (err) {
      addToast({ type: 'error', title: 'Could not load team', message: err.message });
      setAssignRoster([]);
    } finally {
      setAssignLoadingRoster(false);
    }
  };

  useEffect(() => {
    // Directory cache is shared; ensure it is warm for name resolution + co-worker picker.
    void loadUsersDirectory().catch(() => {});
  }, [lead?.agency_id]);

  const handleCoWorkerChange = async (coWorkerId) => {
    setCoWorkerSaving(true);
    try {
      const updated = await api.leads.updateCoWorker(id, coWorkerId);
      setLead(updated);
      addToast({ type: 'success', title: 'Co-worker updated' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to update co-worker',
        message: err?.message || 'Try again.',
      });
    } finally {
      setCoWorkerSaving(false);
    }
  };

  const handleSaveAssignments = async () => {
    setAssignSaving(true);
    try {
      const updated = await api.leads.setAssignments(id, {
        setterUserId: assignSetterId || null,
        closerUserId: assignCloserId || null,
      });
      setLead(updated);
      addToast({ type: 'success', title: 'Assignments saved' });
      setAssignModalOpen(false);
    } catch (err) {
      addToast({ type: 'error', title: 'Save failed', message: err.message });
    } finally {
      setAssignSaving(false);
    }
  };

  const handleEditSave = async (changedFields) => {
    try {
      await api.leads.update(id, changedFields);
      setEditDrawerOpen(false);
      addToast({ type: 'success', title: 'Lead updated successfully' });
      await loadLead(true);
    } catch {
      addToast({ type: 'error', title: 'Failed to save changes' });
    }
  };

  const openLogCallModal = () => {
    setLogCallOutcome('');
    setLogCallDirection('outbound');
    setLogCallMinutes('0');
    setLogCallSeconds('0');
    setLogCallAtLocal(toDatetimeLocalValue(new Date()));
    setLogCallNotes('');
    setLogCallRoomType(lead?.room_type ?? null);
    setLogCallOpen(true);
  };

  const handleSaveManualCallLog = async () => {
    if (!logCallOutcome) {
      addToast({
        type: 'error',
        title: 'Outcome required',
        message: 'Select an outcome before saving the call log.',
      });
      return;
    }
    const m = Math.max(0, parseInt(logCallMinutes, 10) || 0);
    const s = Math.min(59, Math.max(0, parseInt(logCallSeconds, 10) || 0));
    const duration_seconds = m * 60 + s;
    const parsed = new Date(logCallAtLocal);
    if (Number.isNaN(parsed.getTime())) {
      addToast({ type: 'error', title: 'Invalid date', message: 'Check the date and time for this call.' });
      return;
    }
    const called_at = parsed.toISOString();
    setLogCallSaving(true);
    try {
      await api.calls.log(id, {
        direction: logCallDirection,
        outcome: logCallOutcome,
        duration_seconds,
        notes: logCallNotes.trim() || null,
        called_at,
        room_type: logCallRoomType || null,
      });
      const stats = await api.callLogs.getDialingStatsForLead(id);
      await api.leads.update(id, {
        call_count: stats.totalCalls,
        last_called_at: stats.lastCalledAt,
        calls_last_5min: stats.callsLast5Min,
      });
      setDialingStats(stats);
      setLogCallOpen(false);
      addToast({ type: 'success', title: 'Call logged' });
      await loadLead(true);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not log call', message: err.message });
    } finally {
      setLogCallSaving(false);
    }
  };

  const handleQuickSmsSend = async () => {
    const body = quickSmsBody.trim();
    if (!body || !lead?.phone) return;
    setQuickSmsSending(true);
    try {
      const user = viewer || await api.auth.getCurrentUser();
      const preferredDialer =
        user?.preferred_dialer === 'telnyx' || user?.preferred_dialer === 'twilio'
          ? user.preferred_dialer
          : 'twilio';
      const result = await api.calls.sendSms({
        to: lead.phone.trim(),
        message: body,
        lead_id: id,
        agency_id: lead.agency_id,
        preferredDialer,
      });
      // twilio-sms already inserts into sms_messages; saveOutbound only for telnyx.
      if (preferredDialer === 'telnyx') {
        try {
          await api.smsMessages.saveOutbound({
            lead_id: id,
            to_number: lead.phone.trim(),
            from_number: result?.from || '',
            body,
            twilio_message_sid: result?.sid || null,
          });
        } catch { /* ignore duplicate persistence errors */ }
      }
      addToast({ type: 'success', title: 'SMS sent' });
      setQuickSmsBody('');
    } catch (err) {
      addToast({ type: 'error', title: 'SMS failed', message: err.message });
    } finally {
      setQuickSmsSending(false);
    }
  };

  const sendEmailViaBrevo = async ({ subject, html }) => {
    if (!subject || !html || !lead?.email) return false;
    setEmailSending(true);
    try {
      const toName = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.email;
      const result = await api.email.send({
        to_email: lead.email.trim(),
        to_name: toName,
        subject,
        html_body: html,
      });
      await api.emailLogs.create({
        lead_id: id,
        to_email: lead.email.trim(),
        subject,
        body_html: html,
        brevo_message_id: result.messageId,
      });
      addToast({ type: 'success', title: 'Email sent' });
      await refreshEmailLogs();
      return true;
    } catch (err) {
      addToast({ type: 'error', title: 'Email failed', message: err.message });
      return false;
    } finally {
      setEmailSending(false);
    }
  };

  const scheduleEmail = async ({ subject, html, sendAt }) => {
    if (!subject || !html || !sendAt || !lead?.email) return false;
    setEmailSending(true);
    try {
      const row = await api.scheduledEmails.create({
        leadId: id,
        toEmail: lead.email.trim(),
        subject,
        bodyHtml: html,
        sendAt,
      });
      addToast({
        type: 'success',
        title: `Email scheduled for ${formatScheduledEt(row?.send_at || sendAt)}`,
        message: 'Manage it from the Email tab.',
      });
      setScheduleMode('now');
      setScheduleValue(tomorrowAt9Value());
      await refreshScheduledEmails();
      return true;
    } catch (err) {
      addToast({ type: 'error', title: "Couldn't schedule the email", message: err.message });
      return false;
    } finally {
      setEmailSending(false);
    }
  };

  const handleCancelScheduledEmail = async (scheduledId) => {
    try {
      await api.scheduledEmails.cancel(scheduledId);
      addToast({ type: 'success', title: 'Scheduled email cancelled' });
    } catch (err) {
      addToast({ type: 'error', title: "Couldn't cancel the email", message: err.message });
    }
    await refreshScheduledEmails();
  };

  const handleRescheduleScheduledEmail = async (scheduledId, sendAt) => {
    let ok = false;
    try {
      const row = await api.scheduledEmails.reschedule(scheduledId, sendAt);
      addToast({ type: 'success', title: `Rescheduled for ${formatScheduledEt(row?.send_at || sendAt)}` });
      ok = true;
    } catch (err) {
      addToast({ type: 'error', title: "Couldn't reschedule the email", message: err.message });
    }
    await refreshScheduledEmails();
    return ok;
  };

  const quickScheduleCheck = scheduleMode === 'schedule' ? validateScheduleValue(scheduleValue) : null;

  const handleQuickEmailSend = async () => {
    const body = emailBody.trim();
    const payload = {
      subject: emailSubject.trim(),
      html: body ? sanitizeEmailHtml(plainTextToHtml(body)) : '',
    };
    const done = scheduleMode === 'schedule'
      ? await scheduleEmail({ ...payload, sendAt: quickScheduleCheck?.iso })
      : await sendEmailViaBrevo(payload);
    if (done) {
      setEmailSubject('');
      setEmailBody('');
    }
  };

  const handleComposeSchedule = async ({ subject, html, sendAt }) => {
    if (await scheduleEmail({ subject, html, sendAt })) clearEmailDraft();
  };

  const handleQuickGmailSend = () => {
    const subject = emailSubject.trim();
    const body = emailBody.trim();
    if (!subject || !body || !lead?.email) return;
    gmail.start({ html: sanitizeEmailHtml(plainTextToHtml(body)), subject });
  };

  const handleComposeBrevoSend = async ({ subject, html }) => {
    if (await sendEmailViaBrevo({ subject, html })) clearEmailDraft();
  };

  const handleComposeGmailSend = ({ subject, html }) => {
    if (!subject || !html || !lead?.email) return;
    gmail.start({ html, subject });
  };

  const openCompose = () => {
    if (!composeBody.trim() && emailBody.trim()) setComposeBody(emailBody);
    setComposeOpen(true);
  };

  const smsQuickTemplates = useMemo(
    () =>
      lead
        ? buildSmsQuickReplyTemplates(lead.channel, smsLanguage, lead.first_name, viewer?.full_name)
        : [],
    [lead, smsLanguage, viewer?.full_name],
  );

  useEffect(() => {
    setSmsLanguage(lead?.language === 'es' ? 'es' : 'en');
  }, [lead?.id]);

  useEffect(() => {
    const el = quickSmsTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 56), 120)}px`;
  }, [quickSmsBody]);

  const nextAppointment = useMemo(() => {
    const now = Date.now();
    return (appointments || [])
      .filter(
        (a) =>
          ['scheduled', 'confirmed'].includes(a.status) &&
          new Date(a.scheduled_at).getTime() > now,
      )
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0] ?? null;
  }, [appointments]);

  if (loading || !lead) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-gray-500 text-sm">Loading lead details…</p>
      </div>
    );
  }

  const leadName = `${lead.first_name} ${lead.last_name}`;

  return (
    <>
      <DncConfirmModal
        isOpen={showDncModal}
        onConfirm={handleDncConfirm}
        onCancel={handleDncCancel}
        leadName={leadName}
      />

      <ArchiveConfirmModal
        isOpen={showArchiveModal}
        onConfirm={handleArchiveConfirm}
        onCancel={() => setShowArchiveModal(false)}
        leadName={leadName}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
        leadName={leadName}
        isDeleting={isDeleteInProgress}
      />

      <Modal
        isOpen={assignModalOpen}
        onClose={() => !assignSaving && setAssignModalOpen(false)}
        title="Manage assignments"
        className="max-w-lg"
      >
        <p className="text-sm text-gray-500 mb-4">
          Choose a setter and closer for this lead. Leave a role unassigned to clear it.
        </p>
        {assignLoadingRoster ? (
          <div className="flex items-center gap-2 py-6 text-gray-500 text-sm">
            <Spinner className="text-primary" />
            Loading team…
          </div>
        ) : (
          <div className="space-y-4">
            <Select
              label="Setter"
              value={assignSetterId}
              onChange={(e) => setAssignSetterId(e.target.value)}
              disabled={assignSaving}
              options={[
                { label: '— Unassigned —', value: '' },
                ...assignRoster.map((u) => ({
                  value: u.id,
                  label: u.full_name || 'Unknown',
                })),
              ]}
            />
            <Select
              label="Closer"
              value={assignCloserId}
              onChange={(e) => setAssignCloserId(e.target.value)}
              disabled={assignSaving}
              options={[
                { label: '— Unassigned —', value: '' },
                ...assignRoster.map((u) => ({
                  value: u.id,
                  label: u.full_name || 'Unknown',
                })),
              ]}
            />
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setAssignModalOpen(false)} disabled={assignSaving}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSaveAssignments}
            disabled={assignSaving || assignLoadingRoster}
          >
            {assignSaving ? (
              <>
                <Spinner className="mr-2" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={smsModalOpen} onClose={() => !smsSending && setSmsModalOpen(false)} title="Send SMS">
        <p className="text-sm text-gray-500 mb-3">
          Message will be sent to{' '}
          <span className="font-medium text-gray-800">{lead.phone || '—'}</span>
        </p>
        <textarea
          className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[120px] resize-y"
          value={smsBody}
          onChange={(e) => setSmsBody(e.target.value)}
          disabled={smsSending}
          placeholder="Your message…"
        />
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setSmsModalOpen(false)} disabled={smsSending}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSendSms}
            disabled={smsSending || !smsBody.trim() || !lead.phone}
          >
            {smsSending ? <><Spinner className="mr-2" />Sending…</> : 'Send'}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={logCallOpen}
        onClose={() => !logCallSaving && setLogCallOpen(false)}
        title="Log call"
        className="max-w-lg"
      >
        <div className="space-y-4">
          <Select
            label="Outcome"
            value={logCallOutcome}
            onChange={(e) => setLogCallOutcome(e.target.value)}
            disabled={logCallSaving}
            options={[
              { label: '— Select outcome —', value: '' },
              ...LOG_CALL_OUTCOMES.map((o) => ({ label: o.label, value: o.value })),
            ]}
          />
          <Select
            label="Direction"
            value={logCallDirection}
            onChange={(e) => setLogCallDirection(e.target.value)}
            disabled={logCallSaving}
            options={[
              { label: 'Outbound', value: 'outbound' },
              { label: 'Inbound', value: 'inbound' },
            ]}
          />
          <RoomTypeSelector
            value={logCallRoomType}
            onChange={setLogCallRoomType}
            disabled={logCallSaving}
          />
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1.5">Duration</span>
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <label className="text-xs text-gray-500">Minutes</label>
                <input
                  type="number"
                  min={0}
                  className="w-full mt-0.5 border border-gray-200 rounded-md px-2 py-1.5 text-sm"
                  value={logCallMinutes}
                  onChange={(e) => setLogCallMinutes(e.target.value)}
                  disabled={logCallSaving}
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500">Seconds</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  className="w-full mt-0.5 border border-gray-200 rounded-md px-2 py-1.5 text-sm"
                  value={logCallSeconds}
                  onChange={(e) => setLogCallSeconds(e.target.value)}
                  disabled={logCallSaving}
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Date & time</label>
            <input
              type="datetime-local"
              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
              value={logCallAtLocal}
              onChange={(e) => setLogCallAtLocal(e.target.value)}
              disabled={logCallSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
            <textarea
              className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[80px] resize-y"
              value={logCallNotes}
              onChange={(e) => setLogCallNotes(e.target.value)}
              disabled={logCallSaving}
              placeholder="Optional notes…"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setLogCallOpen(false)} disabled={logCallSaving}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSaveManualCallLog}
            disabled={logCallSaving}
          >
            {logCallSaving ? (
              <>
                <Spinner className="mr-2" />
                Saving…
              </>
            ) : (
              'Save Call Log'
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={transferModalOpen}
        onClose={() => !transferSubmitting && setTransferModalOpen(false)}
        title="Request Ownership Transfer"
        className="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-4">
          Submit a request to transfer ownership of this lead to you. The current owner or an admin
          must approve it.
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Notes <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[80px] resize-y mb-4"
          value={transferNotes}
          onChange={(e) => setTransferNotes(e.target.value)}
          disabled={transferSubmitting}
          placeholder="Reason for transfer…"
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTransferModalOpen(false)}
            disabled={transferSubmitting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => void handleSubmitTransferRequest()}
            disabled={transferSubmitting}
          >
            {transferSubmitting ? (
              <>
                <Spinner className="mr-2" />
                Submitting…
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </div>
      </Modal>

      <div className="space-y-6 pb-24">
        <Card className="p-6">
          <div className="flex flex-col md:flex-row justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 flex-shrink-0">
                <SafeIcon icon={FiUser} className="w-8 h-8" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <button
                    onClick={() => navigate(getBackPath())}
                    title="Back to list"
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  >
                    <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                  </button>
                  <h1 className="text-2xl font-bold text-gray-900">{leadName}</h1>
                  {getChannelBadge(lead.channel)}
                  {getLeadTypeBadge(lead.lead_type)}
                  <div className="relative" ref={roomTypePopoverRef}>
                    <button
                      type="button"
                      onClick={() => setRoomTypePopoverOpen((o) => !o)}
                      className="inline-flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40"
                      title="Lead Type ID (Read the Room)"
                    >
                      {lead.room_type ? (
                        <RoomTypeBadge value={lead.room_type} />
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-400">
                          + Type
                        </span>
                      )}
                    </button>
                    {roomTypePopoverOpen ? (
                      <div className="absolute left-0 top-full mt-2 z-30 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[220px]">
                        <RoomTypeSelector
                          value={lead.room_type ?? null}
                          onChange={(v) => void handleLeadRoomTypeChange(v)}
                          disabled={roomTypeSaving}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-2">
                  {lead.phone && (
                    <span className="flex items-center gap-1.5">
                      <SafeIcon icon={FiPhone} className="text-gray-400" />{lead.phone}
                    </span>
                  )}
                  {lead.email && (
                    <span className="flex items-center gap-1.5">
                      <SafeIcon icon={FiMail} className="text-gray-400" />{lead.email}
                    </span>
                  )}
                  <LanguageBadge language={lead.language} />
                  <span className="flex items-center gap-1.5">
                    <SafeIcon icon={FiClock} className="text-gray-400" />
                    Added {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                  </span>
                  {lead.dob && (
                    <span className="text-gray-500">
                      Age {differenceInYears(new Date(), new Date(lead.dob))}
                    </span>
                  )}
                  {lead.gender && <Badge>{lead.gender}</Badge>}
                </div>
                {(lead.address || lead.city || lead.state) && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1.5">
                    <SafeIcon icon={FiMapPin} className="text-gray-400 w-3.5 h-3.5" />
                    {[lead.address, lead.city, lead.state].filter(Boolean).join(', ')}
                    {lead.zip ? ` ${lead.zip}` : ''}
                  </div>
                )}
                {lead.lead_source === 'purchased' && lead.lead_vendor && (
                  <div className="text-xs text-gray-500 mt-1">
                    Source: Purchased via <span className="font-medium text-gray-700">{lead.lead_vendor}</span>
                  </div>
                )}
                <LeadPeopleSummary lead={lead} />
                <p className="text-xs text-gray-500 mt-2">
                  📞 Total Calls: {dialingStats.totalCalls} · Last Called:{' '}
                  {formatLastCalledDisplay(dialingStats.lastCalledAt)}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 min-w-[200px]">
              <div className="relative">
                <Select
                  value={lead.status}
                  onChange={handleStatusChange}
                  options={[
                    { label: 'New Lead', value: 'new' },
                    { label: 'Contacted', value: 'contacted' },
                    { label: 'Follow Up', value: 'follow_up' },
                    { label: 'Appointment Set', value: 'appointment_set' },
                    { label: 'Closed Won', value: 'closed_won' },
                    { label: 'Closed Lost', value: 'closed_lost' },
                    { label: 'Do Not Contact', value: 'dnc' },
                  ]}
                  className={`font-medium pr-8 ${lead.status === 'dnc' ? 'text-red-600 border-red-300 bg-red-50' : ''}`}
                  disabled={updatingStatus}
                />
                {updatingStatus && <Spinner className="absolute right-8 top-3 text-primary" />}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setDialerOpen(true)}
                  disabled={!lead.phone}
                  title={!lead.phone ? 'Add a phone number on the lead to call' : 'Open softphone'}
                  className="flex-1 min-w-[100px]"
                  variant="primary"
                >
                  <SafeIcon icon={FiPhone} className="mr-2" />Call
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 min-w-[100px]"
                  onClick={() => navigate(`/workspace?lead=${id}`, {
                    state: location.state?.leadIds ? { leadIds: location.state.leadIds, currentIndex: location.state.currentIndex } : undefined,
                  })}
                  title="Open Workspace"
                >
                  <SafeIcon icon={FiTool} className="mr-2" />Open Workspace
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 min-w-[100px]"
                  onClick={openLogCallModal}
                  title="Log a call manually"
                >
                  <SafeIcon icon={FiPhoneCall} className="mr-2" />Log Call
                </Button>
                {lead.phone?.trim() && (
                  <Button
                    variant="outline"
                    className="flex-1 min-w-[100px]"
                    onClick={() => setAiCallerModalOpen(true)}
                    title="Send this lead to the Thoughtly AI caller"
                  >
                    <SafeIcon icon={FiZap} className="mr-2" />
                    Send to AI Caller
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="flex-1 min-w-[100px]"
                  onClick={() => setLogActivityOpen(true)}
                  title="Log presentation, app, referral, objection, or manual calls"
                >
                  <SafeIcon icon={FiActivity} className="mr-2" />Log Activity
                </Button>
                <Button variant="outline" className="flex-1 min-w-[100px]" onClick={() => setEditDrawerOpen(true)} title="Edit lead">
                  <SafeIcon icon={FiEdit2} className="mr-2" />Edit
                </Button>
                {!lead.archived_at && !lead.merged_into && (
                  <Button
                    variant="outline"
                    className="flex-1 min-w-[100px]"
                    onClick={handleFindDuplicate}
                    disabled={duplicatesLoading || mergeCandidateLoading}
                    title="Find another lead with the same phone or email"
                  >
                    {duplicatesLoading ? (
                      <Spinner className="mr-2 w-4 h-4" />
                    ) : (
                      <SafeIcon icon={FiGitMerge} className="mr-2" />
                    )}
                    Find Duplicate
                  </Button>
                )}
              </div>
              {duplicateMessage && (
                <p className="text-xs text-gray-500 mt-1">{duplicateMessage}</p>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left sidebar */}
          <div className="space-y-6">
            <Card className="p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="text-primary" />
                Appointments
              </h3>
              {nextAppointment ? (
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-medium text-gray-900">
                    {format(parseISO(nextAppointment.scheduled_at), 'EEE, MMM d')} at{' '}
                    {format(parseISO(nextAppointment.scheduled_at), 'h:mm a')}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <AppointmentTypeBadge type={nextAppointment.appointment_type} />
                  </div>
                  <p className="text-xs text-gray-500">
                    Assigned to{' '}
                    <span className="font-medium text-gray-700">
                      {resolveDisplayName(
                        nextAppointment.assigned_to,
                        nextAppointment.assignee,
                      ) || 'Agent'}
                    </span>
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic mb-4">No upcoming appointments</p>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  setBookModalAppt(null);
                  setBookModalOpen(true);
                }}
              >
                <SafeIcon icon={FiCalendar} className="mr-2" />
                Book Appointment
              </Button>
            </Card>

            <Card className="p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiPieChart} className="text-primary" />
                Financial Analysis
              </h3>
              {fnaLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <Spinner className="text-primary" />
                  Loading…
                </div>
              ) : fnaError ? (
                <p className="text-sm text-red-600 mb-3">{fnaError}</p>
              ) : fna ? (
                <>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Net Worth</span>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0,
                        }).format(calculateNetWorth(fna.assets, fna.liabilities))}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Needs Attention</span>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {`${calculateEstateNeedsAttentionCount(fna.estate_checklist)} of 8`}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => navigate(`/financial-analysis/${fna.id}`)}
                  >
                    View Financial Analysis
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 italic mb-4">
                    No financial analysis started yet for this lead.
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-full"
                    disabled={fnaStarting}
                    onClick={async () => {
                      if (!lead?.id || !lead?.agency_id) return;
                      setFnaStarting(true);
                      try {
                        const created = await api.financialAnalysis.create({
                          leadId: lead.id,
                          agencyId: lead.agency_id,
                          userId: viewer?.id || null,
                        });
                        if (!created?.id) throw new Error('Create returned no analysis');
                        setFna(created);
                        navigate(`/financial-analysis/${created.id}`);
                      } catch (err) {
                        addToast({
                          type: 'error',
                          title: 'Could not start financial analysis',
                          message: err?.message,
                        });
                      } finally {
                        setFnaStarting(false);
                      }
                    }}
                  >
                    {fnaStarting ? (
                      <>
                        <Spinner className="mr-2" />
                        Starting…
                      </>
                    ) : (
                      'Start Financial Analysis'
                    )}
                  </Button>
                </>
              )}
            </Card>

            <Card className="p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiMessageSquare} className="text-primary" />
                Send SMS
              </h3>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-medium text-gray-600">Language</span>
                <div className="inline-flex gap-2">
                  {[
                    { value: 'en', label: 'EN' },
                    { value: 'es', label: 'ES' },
                  ].map((opt) => {
                    const active = smsLanguage === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={!lead.phone || quickSmsSending}
                        onClick={() => setSmsLanguage(opt.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all disabled:opacity-50 ${
                          active
                            ? 'bg-[#00AAFF] text-white border-[#00AAFF]'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-[#00AAFF]/40'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {smsQuickTemplates.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    disabled={!lead.phone || quickSmsSending}
                    onClick={() => setQuickSmsBody(t.text)}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700 hover:bg-primary/10 hover:border-primary/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <textarea
                ref={quickSmsTextareaRef}
                rows={2}
                className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none overflow-hidden min-h-[56px] max-h-[120px]"
                placeholder={lead.phone ? 'Message…' : 'Add a phone number to send SMS'}
                value={quickSmsBody}
                onChange={(e) => setQuickSmsBody(e.target.value)}
                disabled={!lead.phone || quickSmsSending}
              />
              <div className="flex items-center justify-between mt-1.5 gap-2">
                <span className="text-xs text-gray-400">{quickSmsBody.length}/160</span>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!lead.phone || quickSmsSending || !quickSmsBody.trim()}
                  onClick={handleQuickSmsSend}
                >
                  {quickSmsSending ? <><Spinner className="mr-2" />Sending…</> : 'Send'}
                </Button>
              </div>
            </Card>

            {lead.email?.trim() ? (
              <Card className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <SafeIcon icon={FiMail} className="text-primary" />
                    Send Email
                  </h3>
                  <button
                    type="button"
                    onClick={openCompose}
                    disabled={emailSending}
                    className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-skyTint/60 disabled:opacity-40"
                    title="Expand — compose with HTML and preview"
                    aria-label="Expand email composer"
                  >
                    <SafeIcon icon={FiMaximize2} />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3 truncate" title={lead.email}>
                  To: {lead.email}
                </p>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-md px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none mb-2"
                  placeholder="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={emailSending}
                />
                <textarea
                  rows={4}
                  className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-y min-h-[88px]"
                  placeholder="Message…"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  disabled={emailSending}
                />
                {!composeOpen ? <GmailSendNotice gmail={gmail} className="mt-2" /> : null}
                <div className="mt-2">
                  <ScheduleSendControl
                    mode={scheduleMode}
                    onModeChange={handleScheduleModeChange}
                    value={scheduleValue}
                    onValueChange={setScheduleValue}
                    disabled={emailSending}
                    compact
                  />
                </div>
                {scheduleMode === 'schedule' ? (
                  <p className="text-[11px] text-gray-500 mt-1.5">{GMAIL_SCHEDULE_NOTE}</p>
                ) : null}
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={
                      emailSending || gmail.busy || scheduleMode === 'schedule'
                      || !emailSubject.trim() || !emailBody.trim()
                    }
                    onClick={handleQuickGmailSend}
                    title={
                      scheduleMode === 'schedule'
                        ? GMAIL_SCHEDULE_NOTE
                        : 'Copies the email and opens Gmail — opens are tracked approximately'
                    }
                  >
                    {gmail.busy ? <><Spinner className="mr-2" />Opening…</> : 'Send with Gmail'}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={
                      emailSending || gmail.busy || !emailSubject.trim() || !emailBody.trim()
                      || (scheduleMode === 'schedule' && !quickScheduleCheck?.iso)
                    }
                    onClick={handleQuickEmailSend}
                  >
                    {scheduleMode === 'schedule'
                      ? (emailSending ? <><Spinner className="mr-2" />Scheduling…</> : 'Schedule send')
                      : (emailSending ? <><Spinner className="mr-2" />Sending…</> : 'Send Email')}
                  </Button>
                </div>
                <EmailComposeModal
                  isOpen={composeOpen}
                  onClose={() => setComposeOpen(false)}
                  toEmail={lead.email.trim()}
                  subject={emailSubject}
                  onSubjectChange={setEmailSubject}
                  body={composeBody}
                  onBodyChange={setComposeBody}
                  sending={emailSending}
                  gmailBusy={gmail.busy}
                  onSendBrevo={handleComposeBrevoSend}
                  onSendGmail={handleComposeGmailSend}
                  onSchedule={handleComposeSchedule}
                  scheduleMode={scheduleMode}
                  onScheduleModeChange={handleScheduleModeChange}
                  scheduleValue={scheduleValue}
                  onScheduleValueChange={setScheduleValue}
                  notice={<GmailSendNotice gmail={gmail} />}
                />
              </Card>
            ) : null}

            <Card className="p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Assignments</h3>
              <div className="space-y-3">
                {lead.assignments?.length > 0 ? (
                  lead.assignments.map((a) => {
                    const displayName =
                      resolveDisplayName(a.user_id, a.user) || 'Unknown agent';
                    const initial = (displayName || '?').charAt(0).toUpperCase();
                    return (
                      <div key={a.id} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center text-xs font-bold">
                          {initial}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{displayName}</p>
                          <p className="text-xs text-gray-500 capitalize">{a.assignment_role}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-400 italic text-center py-4">No assigned agents yet.</p>
                )}
                <div className="border-t border-gray-100 pt-3 mt-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Co-Worker</p>
                  {canManageCoWorker ? (
                    <div className={coWorkerSaving ? 'opacity-60 pointer-events-none' : ''}>
                      <UserPickerDropdown
                        source="directory"
                        value={lead.co_worker_id || null}
                        onChange={handleCoWorkerChange}
                        placeholder="None"
                        disabled={coWorkerSaving}
                      />
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-gray-900">{coWorkerDisplayName(lead)}</p>
                  )}
                </div>
                {canManageAssignments ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={openAssignModal}
                  >
                    Manage Assignments
                  </Button>
                ) : (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Ask a manager or admin to assign a setter or closer.
                  </p>
                )}
                {(canRequestOwnershipTransfer || pendingTransferRequest) && (
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    {pendingTransferRequest ? (
                      <Badge variant="warning">Transfer Requested</Badge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setTransferModalOpen(true)}
                      >
                        Request Ownership Transfer
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>

            <ExclusivityPanel
              lead={lead}
              leadId={id}
              viewer={viewer}
              canManage={canManageAssignments}
              onLeadUpdated={() => loadLead(true)}
            />
          </div>

          {/* Right column: Activity / Notes + Academy Data */}
          <div className="lg:col-span-2 space-y-6">
          <Card className="flex flex-col h-[600px]">
            <div className="border-b border-gray-200">
              <div className="flex overflow-x-auto" role="tablist">
                {LEAD_TABS.map(({ id: tab, label }) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab}
                    className={`grow shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
              {activeTab === 'activity' && (
                <LeadActivityFeed
                  lead={lead}
                  appointments={appointments}
                  thoughtlyCalls={thoughtlyCalls}
                  thoughtlyAgents={thoughtlyAgents}
                  refreshToken={lastRefreshed?.getTime() ?? 0}
                  lastRefreshed={lastRefreshed}
                  refreshing={activityRefreshing}
                  onRefresh={refreshActivity}
                  onOpenTab={openTab}
                  onCallLead={() => setDialerOpen(true)}
                  hasPhone={!!lead.phone}
                />
              )}

              {activeTab === 'calls' && (
                <CallsTab
                  filter={callsFilter}
                  onFilterChange={setCallsFilter}
                  callLogs={lead.callLogs ?? []}
                  thoughtlyCalls={thoughtlyCalls}
                  thoughtlyAgents={thoughtlyAgents}
                  thoughtlyLoading={thoughtlyLoading}
                  hasPhone={!!lead.phone?.trim()}
                  onCallLead={() => setDialerOpen(true)}
                  onSendToAiCaller={() => setAiCallerModalOpen(true)}
                />
              )}

              {activeTab === 'sms' && (
                <div className="flex flex-col h-full -m-6">
                  <div className="flex-1 overflow-y-auto">
                    <SMSThread
                      messages={smsThread}
                      loading={smsThreadLoading}
                      emptyAction={
                        lead.phone ? (
                          <Button size="sm" className="mt-4" onClick={() => setSmsModalOpen(true)}>
                            <SafeIcon icon={FiMessageSquare} className="mr-2" />
                            Send SMS
                          </Button>
                        ) : null
                      }
                    />
                  </div>
                  {lead.phone && !smsThreadLoading && (
                    <SMSInputBar
                      disabled={!lead.phone}
                      onSend={async (body) => {
                        try {
                          const user = viewer || await api.auth.getCurrentUser();
                          const preferredDialer =
                            user?.preferred_dialer === 'telnyx' || user?.preferred_dialer === 'twilio'
                              ? user.preferred_dialer
                              : 'twilio';
                          const result = await api.calls.sendSms({
                            to: lead.phone.trim(),
                            message: body,
                            lead_id: id,
                            agency_id: lead.agency_id,
                            preferredDialer,
                          });
                          // twilio-sms already inserts into sms_messages; saveOutbound only for telnyx.
                          // Realtime subscription is the sole source of the thread row (dedupes by real DB id).
                          if (preferredDialer === 'telnyx') {
                            try {
                              await api.smsMessages.saveOutbound({
                                lead_id: id,
                                to_number: lead.phone.trim(),
                                from_number: result?.from || '',
                                body,
                                twilio_message_sid: result?.sid || null,
                              });
                            } catch { /* ignore duplicate persistence errors */ }
                          }
                          addToast({ type: 'success', title: 'SMS sent' });
                        } catch (err) {
                          addToast({ type: 'error', title: 'SMS failed', message: err.message });
                          throw err;
                        }
                      }}
                    />
                  )}
                </div>
              )}

              {activeTab === 'sequences' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-500">
                      {enrollments.length} enrollment{enrollments.length !== 1 ? 's' : ''}
                    </p>
                    <span
                      title={
                        lead.email?.trim()
                          ? undefined
                          : 'Add an email address to this lead before enrolling in a sequence.'
                      }
                      className="inline-block"
                    >
                      <Button
                        size="sm"
                        onClick={() => void openEnrollModal()}
                        disabled={!lead.email?.trim()}
                      >
                        Enroll in Sequence
                      </Button>
                    </span>
                  </div>

                  {enrollmentsLoading ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
                      <Spinner className="text-primary" />
                      Loading enrollments…
                    </div>
                  ) : enrollments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <SafeIcon icon={FiMail} className="w-10 h-10 text-gray-300 mb-3" />
                      <p className="text-sm font-medium text-gray-600">No sequence enrollments</p>
                      <p className="text-xs text-gray-400 mt-1 max-w-xs">
                        {lead.email?.trim()
                          ? 'Enroll this lead in a drip sequence manually or via status triggers.'
                          : 'Add an email address to enroll this lead in a sequence.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {enrollments.map((enr) => (
                        <div
                          key={enr.id}
                          className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h4 className="text-sm font-semibold text-gray-900">{enr.sequence_name}</h4>
                                <EnrollmentStatusBadge status={enr.status} />
                              </div>
                              <p className="text-sm text-gray-600">{enr.current_step_label}</p>
                              {enr.current_step_subject && (
                                <p className="text-xs text-gray-400 mt-0.5 truncate">
                                  {enr.current_step_subject}
                                </p>
                              )}
                            </div>
                            {enr.status === 'active' && MANAGER_ROLES.has(viewer?.role) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => void handleCancelEnrollment(enr.id)}
                                disabled={cancellingEnrollmentId === enr.id}
                                isLoading={cancellingEnrollmentId === enr.id}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-500">
                            {enr.status === 'active' && enr.next_send_at && (
                              <div>
                                <dt className="font-medium text-gray-600">Next send</dt>
                                <dd>{formatEnrollmentTimestamp(enr.next_send_at)}</dd>
                              </div>
                            )}
                            <div>
                              <dt className="font-medium text-gray-600">Enrolled</dt>
                              <dd>{formatEnrollmentTimestamp(enr.enrolled_at)}</dd>
                            </div>
                            <div>
                              <dt className="font-medium text-gray-600">Enrolled by</dt>
                              <dd>{enr.enrolled_by_name || (enr.enrolled_by ? 'User' : 'Automatic')}</dd>
                            </div>
                          </dl>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'email' && (
                <>
                  <ScheduledEmailsList
                    rows={scheduledEmails}
                    onCancel={handleCancelScheduledEmail}
                    onReschedule={handleRescheduleScheduledEmail}
                  />
                  <EmailHistoryList
                    logs={emailLogs}
                    loading={emailLogsLoading}
                    emptyHint={
                      lead.email?.trim()
                        ? 'Use the Send Email panel in the sidebar to reach this lead.'
                        : 'Add an email address to this lead to send messages.'
                    }
                  />
                </>
              )}

              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <textarea
                    className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[100px] resize-none"
                    placeholder="Type a new note here…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button size="sm" onClick={handleSaveNote} disabled={savingNote || !note.trim()}>
                      {savingNote ? <><Spinner className="mr-2" />Saving…</> : 'Save Note'}
                    </Button>
                  </div>
                  {lead.notes ? (
                    <div className="bg-yellow-50 p-4 rounded-md border border-yellow-100 text-sm text-gray-800">
                      <p className="font-semibold mb-1 text-yellow-800">Initial Note</p>
                      {lead.notes}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-400 text-sm">No notes yet. Add one above.</div>
                  )}
                </div>
              )}

              {activeTab === 'reminders' && (
                <LeadRemindersPanel lead={lead} viewer={viewer} />
              )}

              {activeTab === 'sessions' && (
                <div className="space-y-4">
                  {sessionsLoading ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
                      <Spinner className="text-primary" />
                      Loading sessions…
                    </div>
                  ) : sessions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <SafeIcon icon={FiTool} className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">No workspace sessions yet</p>
                      <p className="text-xs text-gray-400 mt-1">Sessions are created when you open this lead in the Workspace.</p>
                    </div>
                  ) : (
                    sessions.map((session) => {
                      const isExpanded = expandedSessionId === session.id;
                      const branches = session.branches_taken || [];
                      const branchPath = branches.map((b) => b.branchLabel || b.stepId).join(' → ');
                      const intake = session.intake_data || {};
                      const coverage = session.coverage_calculation || {};
                      const scriptBadgeColor = session.script_type === 'fex'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700';

                      return (
                        <div key={session.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {session.created_at
                                      ? format(new Date(session.created_at), 'MMM d, yyyy — h:mm a')
                                      : '—'}
                                  </span>
                                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${scriptBadgeColor}`}>
                                    {session.script_type === 'fex' ? 'FEX' : 'Ethos'}
                                  </span>
                                </div>
                                {branchPath && (
                                  <p className="text-xs text-gray-500 mb-1">
                                    <span className="font-medium text-gray-600">Path:</span> {branchPath}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                                  {intake.beneficiary && (
                                    <span>Beneficiary: <span className="font-medium text-gray-700">{intake.beneficiary}</span></span>
                                  )}
                                  {(intake.coverageGoal || intake.fexCoverageAmount) && (
                                    <span>Coverage: <span className="font-medium text-gray-700">{intake.coverageGoal || intake.fexCoverageAmount}</span></span>
                                  )}
                                  {coverage.totalInsurableNeed > 0 && (
                                    <span>Need: <span className="font-semibold" style={{ color: '#00AAFF' }}>
                                      ${Math.round(coverage.totalInsurableNeed).toLocaleString()}
                                    </span></span>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setExpandedSessionId(isExpanded ? null : session.id)}
                                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
                              >
                                <SafeIcon
                                  icon={isExpanded ? FiIcons.FiChevronUp : FiIcons.FiChevronDown}
                                  className="w-4 h-4"
                                />
                              </button>
                            </div>
                          </div>

                          {isExpanded && Object.keys(intake).length > 0 && (
                            <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
                              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Intake Data</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                {Object.entries(intake).map(([k, v]) => (
                                  <div key={k} className="flex justify-between items-baseline gap-2">
                                    <span className="text-[11px] text-gray-500 truncate">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
                                    <span className="text-[11px] font-medium text-gray-800 text-right shrink-0">
                                      {v === true ? 'Yes' : v === false ? 'No' : String(v || '—')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

            </div>
          </Card>

          <ProsperityMentorsSection lead={lead} />
          </div>
        </div>
      </div>

      <Modal
        isOpen={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
        title="Enroll in Sequence"
      >
        {enrollModalLoading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-sm text-gray-500">
            <Spinner className="text-primary" />
            Loading sequences…
          </div>
        ) : enrollModalOptions.length === 0 ? (
          <p className="text-sm text-gray-600 py-4">
            No active sequences available. This lead may already be enrolled in all active sequences, or none
            are configured in Settings.
          </p>
        ) : (
          <div className="space-y-4 mt-2">
            <Select
              label="Sequence"
              value={selectedSequenceId}
              onChange={(e) => setSelectedSequenceId(e.target.value)}
              options={[
                { value: '', label: 'Select a sequence…' },
                ...enrollModalOptions.map((seq) => ({
                  value: seq.id,
                  label: seq.name,
                })),
              ]}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEnrollModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleEnrollInSequence()}
                disabled={!selectedSequenceId}
                isLoading={enrolling}
              >
                Enroll
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <LeadNavBar currentId={id} />

      <LogActivityModal
        isOpen={logActivityOpen}
        onClose={() => setLogActivityOpen(false)}
        leadId={id}
        onSuccess={() => loadLead(true)}
      />

      <AiCallerModal
        isOpen={aiCallerModalOpen}
        onClose={() => setAiCallerModalOpen(false)}
        lead={lead}
        leadId={id}
        agencyId={lead.agency_id}
        thoughtlyCalls={thoughtlyCalls}
        onSuccess={() => {
          addToast({
            type: 'success',
            title: 'AI Caller activated — call in progress',
          });
          void loadLead(true);
        }}
      />

      <BookAppointmentModal
        isOpen={bookModalOpen}
        onClose={() => {
          setBookModalOpen(false);
          setBookModalAppt(null);
        }}
        lead={lead}
        existingAppointment={bookModalAppt}
        onSuccess={() => loadLead(true)}
      />

      <EditLeadDrawer
        isOpen={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        lead={lead}
        onSave={handleEditSave}
        canArchive={canArchiveLead}
        canDelete={canDeleteLead}
        onArchive={() => {
          setEditDrawerOpen(false);
          setShowArchiveModal(true);
        }}
        onDelete={() => {
          setEditDrawerOpen(false);
          setShowDeleteModal(true);
        }}
      />

      <Modal
        isOpen={duplicatePickerOpen}
        onClose={() => setDuplicatePickerOpen(false)}
        title="Select duplicate to merge"
        className="max-w-lg"
      >
        <p className="text-sm text-gray-500 mb-4">
          These leads share the same phone or email. Choose one to merge with this record.
        </p>
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
          {duplicates.map((dup) => (
            <li key={dup.id}>
              <button
                type="button"
                className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors"
                onClick={() => void handleSelectDuplicateForMerge(dup)}
              >
                <div className="font-medium text-gray-900">
                  {dup.first_name} {dup.last_name}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                  {dup.phone && <div>{dup.phone}</div>}
                  {dup.email && <div className="truncate">{dup.email}</div>}
                  {dup.lead_type && (
                    <div>{LEAD_TYPE_LABELS[dup.lead_type] || dup.lead_type}</div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </Modal>

      <MergeLeadsModal
        isOpen={mergeModalOpen}
        onClose={() => {
          setMergeModalOpen(false);
          setMergeCandidateB(null);
        }}
        leadA={lead}
        leadB={mergeCandidateB}
        agencyUsers={[]}
        onMerged={handleMergeComplete}
      />

      <Softphone
        open={dialerOpen}
        onClose={() => {
          setDialerOpen(false);
          loadLead(true);
        }}
        leadId={id}
        leadPhone={lead.phone}
        leadName={leadName}
        leadStatus={lead?.status}
        onAfterPostCallSave={() => loadLead(true)}
      />
    </>
  );
}